import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Slider,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { PoseLandmarker, FilesetResolver } from '@mediapipe/tasks-vision';

const POSE_CONNECTIONS = [
  [0, 1], [1, 2], [2, 3], [3, 7], [0, 4], [4, 5], [5, 6], [6, 8], [9, 10],
  [11, 12], [11, 13], [13, 15], [15, 17], [15, 19], [15, 21], [17, 19],
  [12, 14], [14, 16], [16, 18], [16, 20], [16, 22], [18, 20],
  [11, 23], [12, 24], [23, 24],
  [23, 25], [25, 27], [27, 29], [27, 31], [29, 31],
  [24, 26], [26, 28], [28, 30], [28, 32], [30, 32]
];

const VISIBILITY_THRESHOLD = 0.5;
// A usable reference pose needs the torso and both legs; below this the
// landmarks aren't worth saving as the target posture.
const MIN_VISIBLE_LANDMARKS = 20;

const pickMimeType = () =>
  MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';

const seekTo = (video, time) =>
  new Promise((resolve) => {
    if (Math.abs(video.currentTime - time) < 0.001) {
      resolve();
      return;
    }
    const onSeeked = () => {
      video.removeEventListener('seeked', onSeeked);
      resolve();
    };
    video.addEventListener('seeked', onSeeked);
    video.currentTime = time;
  });

// MediaRecorder-produced WebM files report a duration of Infinity until they
// have been seeked to the end once, which also builds the seek cues we need.
const resolveDuration = async (video) => {
  if (video.duration !== Infinity && !Number.isNaN(video.duration)) return video.duration;
  await seekTo(video, 1e101);
  return video.duration;
};

function VideoTrimmerModal({ videoBlob, onSave, onCancel }) {
  const [duration, setDuration] = useState(0);
  const [range, setRange] = useState([0, 0]);
  const [poseFrame, setPoseFrame] = useState(0);
  const [step, setStep] = useState(1);
  const [videoUrl, setVideoUrl] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isModelLoading, setIsModelLoading] = useState(false);
  const [visibleCount, setVisibleCount] = useState(null);
  const [error, setError] = useState('');

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  // Separate landmarker: the shared one runs in VIDEO mode and only accepts
  // detectForVideo() with ever-increasing timestamps, which cannot be used to
  // inspect an arbitrary frame the coach scrubs back and forth to.
  const landmarkerRef = useRef(null);

  useEffect(() => {
    if (!videoBlob) return undefined;
    const url = URL.createObjectURL(videoBlob);
    setVideoUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [videoBlob]);

  useEffect(() => {
    let closed = false;

    async function initLandmarker() {
      setIsModelLoading(true);
      try {
        const vision = await FilesetResolver.forVisionTasks(
          'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.8/wasm'
        );
        const landmarker = await PoseLandmarker.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              'https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task',
            delegate: 'GPU'
          },
          runningMode: 'IMAGE',
          numPoses: 1
        });
        if (closed) {
          landmarker.close();
          return;
        }
        landmarkerRef.current = landmarker;
      } catch (err) {
        console.error('Failed to load pose model for frame selection:', err);
        setError('Could not load the pose model. Reopen this dialog to try again.');
      } finally {
        if (!closed) setIsModelLoading(false);
      }
    }

    initLandmarker();

    return () => {
      closed = true;
      landmarkerRef.current?.close();
      landmarkerRef.current = null;
    };
  }, []);

  const handleLoadedMetadata = async () => {
    const video = videoRef.current;
    if (!video) return;
    const resolved = await resolveDuration(video);
    if (!resolved || Number.isNaN(resolved)) return;
    setDuration(resolved);
    setRange([0, resolved]);
    setPoseFrame(0);
    video.currentTime = 0;
  };

  const detectAt = useCallback((source) => {
    const landmarker = landmarkerRef.current;
    if (!landmarker || !source || !source.videoWidth) return null;
    try {
      const results = landmarker.detect(source);
      return results?.landmarks?.[0] ?? null;
    } catch (err) {
      console.error('Pose detection failed on selected frame:', err);
      return null;
    }
  }, []);

  // Redraw the skeleton over whatever frame the coach has scrubbed to, so the
  // frame they pick is the frame they can see the model agrees with.
  const drawOverlay = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const landmarks = detectAt(video);
    if (!landmarks) {
      setVisibleCount(0);
      return;
    }

    setVisibleCount(landmarks.filter((p) => (p.visibility ?? 0) > VISIBILITY_THRESHOLD).length);

    ctx.strokeStyle = '#4caf50';
    ctx.lineWidth = 4;
    ctx.beginPath();
    POSE_CONNECTIONS.forEach(([i, j]) => {
      const p1 = landmarks[i];
      const p2 = landmarks[j];
      if (!p1 || !p2) return;
      if ((p1.visibility ?? 0) <= VISIBILITY_THRESHOLD || (p2.visibility ?? 0) <= VISIBILITY_THRESHOLD) return;
      ctx.moveTo(p1.x * canvas.width, p1.y * canvas.height);
      ctx.lineTo(p2.x * canvas.width, p2.y * canvas.height);
    });
    ctx.stroke();

    landmarks.forEach((point) => {
      if ((point.visibility ?? 0) <= VISIBILITY_THRESHOLD) return;
      ctx.beginPath();
      ctx.fillStyle = '#4caf50';
      ctx.arc(point.x * canvas.width, point.y * canvas.height, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.stroke();
    });
  }, [detectAt]);

  useEffect(() => {
    if (step === 2 && !isModelLoading) drawOverlay();
  }, [step, isModelLoading, drawOverlay]);

  const handleRangeChange = (event, newValue) => {
    setRange(newValue);
    const clamped = Math.min(Math.max(poseFrame, newValue[0]), newValue[1]);
    setPoseFrame(clamped);
    if (videoRef.current) videoRef.current.currentTime = newValue[0];
  };

  const handlePoseFrameChange = (event, newValue) => {
    setPoseFrame(newValue);
    if (videoRef.current) videoRef.current.currentTime = newValue;
  };

  // Re-encode only the selected span. An untrimmed range keeps the original
  // blob so we never lose quality for nothing.
  const buildTrimmedVideo = async ([start, end]) => {
    if (start <= 0.05 && end >= duration - 0.05) return videoBlob;

    const source = document.createElement('video');
    source.src = videoUrl;
    source.muted = true;
    await new Promise((resolve, reject) => {
      source.onloadeddata = resolve;
      source.onerror = () => reject(new Error('Could not read the recorded video.'));
    });
    await resolveDuration(source);
    await seekTo(source, start);

    const canvas = document.createElement('canvas');
    canvas.width = source.videoWidth || 640;
    canvas.height = source.videoHeight || 480;
    const ctx = canvas.getContext('2d');

    const recorder = new MediaRecorder(canvas.captureStream(30), { mimeType: pickMimeType() });
    const chunks = [];
    recorder.ondataavailable = (e) => {
      if (e.data?.size > 0) chunks.push(e.data);
    };

    const recorded = new Promise((resolve) => {
      recorder.onstop = () => resolve(new Blob(chunks, { type: 'video/webm' }));
    });

    recorder.start();
    await source.play();

    let frameId;
    const finish = () => {
      cancelAnimationFrame(frameId);
      source.pause();
      if (recorder.state === 'recording') recorder.stop();
    };
    const drawFrame = () => {
      if (source.currentTime >= end || source.ended) {
        finish();
        return;
      }
      ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
      frameId = requestAnimationFrame(drawFrame);
    };
    drawFrame();

    // Never let a stalled decode hang the dialog forever.
    const guard = setTimeout(finish, (end - start + 5) * 1000);
    const blob = await recorded;
    clearTimeout(guard);
    return blob;
  };

  const handleSave = async () => {
    setError('');
    setIsProcessing(true);
    try {
      // Read the reference pose first, straight from the chosen frame, so it
      // never depends on the trim re-encode happening to hit that timestamp.
      const video = videoRef.current;
      await seekTo(video, poseFrame);
      const landmarks = detectAt(video);

      const visible = landmarks
        ? landmarks.filter((p) => (p.visibility ?? 0) > VISIBILITY_THRESHOLD).length
        : 0;

      if (!landmarks || visible < MIN_VISIBLE_LANDMARKS) {
        setVisibleCount(visible);
        setError(
          landmarks
            ? `Only ${visible} of 33 body points are clear at this frame. Slide to a frame where your full body is visible.`
            : 'No pose detected at this frame. Slide to a frame where your full body is visible.'
        );
        setIsProcessing(false);
        return;
      }

      // The recording is already mirrored, so these coordinates are in the
      // same space as the video that gets uploaded alongside them.
      const referencePose = landmarks.map((lm) => ({
        x: lm.x,
        y: lm.y,
        z: lm.z || 0,
        visibility: lm.visibility || 0
      }));

      // Extract image blob from clean canvas at this frame
      const cleanCanvas = document.createElement('canvas');
      cleanCanvas.width = video.videoWidth || 640;
      cleanCanvas.height = video.videoHeight || 480;
      const cleanCtx = cleanCanvas.getContext('2d');
      cleanCtx.drawImage(video, 0, 0, cleanCanvas.width, cleanCanvas.height);
      const imageBlob = await new Promise(resolve => cleanCanvas.toBlob(resolve, 'image/jpeg', 0.85));

      const trimmedBlob = await buildTrimmedVideo(range);
      await onSave(trimmedBlob, referencePose, imageBlob);
    } catch (err) {
      console.error('Failed to save pose video:', err);
      setError(err.message || 'Something went wrong while preparing the video.');
      setIsProcessing(false);
    }
  };

  const formatTime = (timeInSeconds) => {
    if (Number.isNaN(timeInSeconds) || !Number.isFinite(timeInSeconds)) return '00:00';
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const generateMarks = (dur) => {
    if (!dur || dur <= 0) return [];
    const marks = [];
    const stepSize = Math.max(1, Math.ceil(dur / 10));
    for (let i = 0; i <= dur; i += stepSize) {
      marks.push({ value: i, label: formatTime(i) });
    }
    if (marks[marks.length - 1].value !== Math.floor(dur)) {
      marks.push({ value: Math.floor(dur), label: formatTime(dur) });
    }
    return marks;
  };

  const poseReady = visibleCount !== null && visibleCount >= MIN_VISIBLE_LANDMARKS;

  return (
    <Dialog open={!!videoBlob} maxWidth="sm" fullWidth disableEscapeKeyDown>
      <DialogTitle sx={{ fontWeight: 'bold' }}>
        {step === 1 ? 'Step 2 of 3 — Trim Your Pose Video' : 'Step 3 of 3 — Select Perfect Pose Frame'}
      </DialogTitle>
      <DialogContent dividers sx={{ overflowY: 'hidden' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Box sx={{ position: 'relative', width: 'fit-content', margin: '0 auto', display: 'flex' }}>
            <video
              ref={videoRef}
              src={videoUrl}
              onLoadedMetadata={handleLoadedMetadata}
              onSeeked={step === 2 ? drawOverlay : undefined}
              controls={step === 1}
              style={{
                maxWidth: '100%',
                maxHeight: '42vh',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              <track kind="captions" srcLang="en" label="english_captions" />
            </video>

            {step === 2 && (
              <canvas
                ref={canvasRef}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none',
                  borderRadius: '8px'
                }}
              />
            )}
          </Box>

          <Box sx={{ width: '100%', px: 1 }}>
            {step === 1 && (
              <>
                <Typography gutterBottom variant="subtitle2" color="text.secondary">
                  Trim Video (Start: {formatTime(range[0])} - End: {formatTime(range[1])})
                </Typography>
                <Slider
                  value={range}
                  onChange={handleRangeChange}
                  valueLabelDisplay="on"
                  valueLabelFormat={formatTime}
                  min={0}
                  max={duration || 100}
                  step={0.1}
                  marks={generateMarks(duration)}
                  disableSwap
                  disabled={isProcessing || duration === 0}
                  sx={{
                    mt: 2,
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: 'primary.main',
                      fontSize: '0.75rem',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }
                  }}
                />
              </>
            )}

            {step === 2 && (
              <>
                <Typography gutterBottom variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                  Select Perfect Pose Frame: {formatTime(poseFrame)}
                </Typography>
                <Typography variant="caption" color="text.disabled" display="block" gutterBottom>
                  Slide to the exact moment your posture is correct — the skeleton shown here is the
                  reference pose students will be scored against.
                </Typography>
                <Slider
                  value={poseFrame}
                  onChange={handlePoseFrameChange}
                  valueLabelDisplay="on"
                  valueLabelFormat={formatTime}
                  min={range[0]}
                  max={Math.max(range[0] + 0.1, range[1])}
                  step={0.1}
                  marks={generateMarks(duration).filter((m) => m.value >= range[0] && m.value <= range[1])}
                  disabled={isProcessing || isModelLoading || duration === 0}
                  sx={{
                    mt: 2,
                    '& .MuiSlider-valueLabel': {
                      backgroundColor: 'secondary.main',
                      fontSize: '0.75rem',
                      padding: '2px 6px',
                      borderRadius: '4px'
                    },
                    '& .MuiSlider-thumb': { backgroundColor: 'secondary.main' },
                    '& .MuiSlider-track': { backgroundColor: 'secondary.main' }
                  }}
                />

                {isModelLoading && (
                  <Alert severity="info" icon={<CircularProgress size={18} />} sx={{ mt: 1 }}>
                    Loading pose model…
                  </Alert>
                )}

                {!isModelLoading && visibleCount !== null && (
                  <Alert severity={poseReady ? 'success' : 'warning'} sx={{ mt: 1 }}>
                    {poseReady
                      ? `Pose captured at this frame — ${visibleCount} of 33 body points clear.`
                      : `Only ${visibleCount} of 33 body points are clear here. Try another frame.`}
                  </Alert>
                )}
              </>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 1 }}>
                {error}
              </Alert>
            )}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, px: 2 }}>
        {step === 1 ? (
          <>
            <Button onClick={onCancel} color="inherit" disabled={isProcessing}>
              Discard &amp; Retake
            </Button>
            <Button
              onClick={async () => {
                setStep(2);
                const video = videoRef.current;
                if (video) {
                  video.pause();
                  await seekTo(video, range[0]);
                  setPoseFrame(range[0]);
                  drawOverlay();
                }
              }}
              variant="contained"
              color="primary"
              disabled={duration === 0}
            >
              Next: Pick Pose Frame
            </Button>
          </>
        ) : (
          <>
            <Button onClick={() => setStep(1)} color="inherit" disabled={isProcessing}>
              Back to Trim
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              color="primary"
              disabled={isProcessing || isModelLoading}
              startIcon={isProcessing ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isProcessing ? 'Processing…' : 'Save & Upload'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default VideoTrimmerModal;
